import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { desc } from "drizzle-orm";
import { Flag, Timer, Trophy } from "lucide-react";
import { getDb } from "@/db";
import { events } from "@/db/schema";
import { authClient } from "@/lib/auth-client";

const getActiveEvent = createServerFn({ method: "GET" }).handler(async () => {
	// Find the most recent active event (qualifying or finals)
	const db = getDb();
	// Get the most recent event by date
	const active = await db
		.select()
		.from(events)
		.orderBy(desc(events.date))
		.limit(1)
		.get();
	return active;
});

export const Route = createFileRoute("/")({
	beforeLoad: async () => {
		const active = await getActiveEvent();
		if (
			active &&
			(active.status === "qualifying" || active.status === "finals")
		) {
			throw redirect({
				to: "/events/$eventId",
				params: { eventId: String(active.id) },
			});
		}
	},
	component: HomePage,
});

function HomePage() {
	const { data: session } = authClient.useSession();

	return (
		<div className="min-h-screen bg-background">
			<section className="py-20 px-6 text-center">
				<div className="max-w-4xl mx-auto">
					<div className="flex items-center justify-center gap-4 mb-6">
						<Flag size={48} className="text-cherry" />
						<h1 className="text-6xl md:text-8xl text-foreground">
							Derby
							<span className="block text-primary">Manager</span>
						</h1>
					</div>
					<p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
						Race timing, leaderboards, and car registration for your pinewood
						derby events.
					</p>
					<div className="flex flex-col sm:flex-row items-center justify-center gap-4">
						<Link
							to="/events"
							className="px-8 py-3 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors text-lg uppercase tracking-wider"
						>
							View Events
						</Link>
						{session?.user ? (
							<Link
								to="/admin/events"
								className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-bold rounded-lg transition-colors text-lg uppercase tracking-wider border border-border"
							>
								Manage Events
							</Link>
						) : (
							<Link
								to="/login"
								className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground font-bold rounded-lg transition-colors text-lg uppercase tracking-wider border border-border"
							>
								Admin Login
							</Link>
						)}
					</div>
				</div>
			</section>

			<div className="checker-stripe h-1 max-w-5xl mx-auto rounded" />

			<section className="py-16 px-6 max-w-5xl mx-auto">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
					<div className="bg-card border-2 border-border rounded-xl p-8 text-center">
						<Flag size={40} className="text-cherry mx-auto mb-4" />
						<h3 className="text-2xl text-foreground mb-2">Event Setup</h3>
						<p className="text-muted-foreground">
							Configure lanes, weight limits, and scoring for each derby.
						</p>
					</div>
					<div className="bg-card border-2 border-border rounded-xl p-8 text-center">
						<Timer size={40} className="text-primary mx-auto mb-4" />
						<h3 className="text-2xl text-foreground mb-2">Race Timing</h3>
						<p className="text-muted-foreground">
							Record race times from your phone. Manual entry or ESP32 sensor.
						</p>
					</div>
					<div className="bg-card border-2 border-border rounded-xl p-8 text-center">
						<Trophy size={40} className="text-gold mx-auto mb-4" />
						<h3 className="text-2xl text-foreground mb-2">Live Leaderboard</h3>
						<p className="text-muted-foreground">
							Auto-updating rankings on the big screen for everyone to watch.
						</p>
					</div>
				</div>
			</section>
		</div>
	);
}
